/**
 * HTTP Store
 *
 * This store is responsible for handling all HTTP requests and responses.
 * It will automatically add the Authorization header to all requests if a token is present.
 */

import { defineStore } from 'pinia'
import axios from 'axios'
import type { ErrorResponse, ErrorResponseDetail } from '@/TcxApi/ErrorResponse'
import _i18n from '@/utilities/I18n'
import globalEvents from '@/utilities/GlobalEvents'
import { computed, ref } from 'vue'

const i18n = _i18n.global

export const useApiStore = defineStore('api', () => {
	const initAxios = function(apiUrl: string, token: string) {
		const http = axios.create()

		http.defaults.timeout = 10000
		http.defaults.baseURL = apiUrl

		http.interceptors.request.use(config => {
			if (token) {
				config.headers.Authorization = `Bearer ${token}`
			}
			return config
		}, null, { synchronous: true })

		http.interceptors.response.use(
			res => { // Success response
				return res
			},
			res => { // Error response
				if (res.response) {
					if (res.response.data) {
						const data: ErrorResponse = res.response.data
						if (data.error && data.error.status === 401 && token) {
							// 401 Unauthorized - we need to reset all auth data
							globalEvents.emit('Auth:LoggedOut', 'HTTP')
						}
						throw res.response.data.error as ErrorResponseDetail // NOSONAR - We just return the TCX error object
					} else {
						throw res
					}
				} else {
					globalEvents.emit('RequestHasFailed')
					const error = new Error(i18n.t('settings.networkError')) as unknown as ErrorResponseDetail

					error.status = 0
					error.code = 'network'
					error.path = 'network'
					throw error
				}
			}
		)

		return http
	}

	const http = ref(initAxios('https://api.telecomx.dk', ''))

	const get = computed(() => http.value.get)
	const post = computed(() => http.value.post)
	const put = computed(() => http.value.put)
	const patch = computed(() => http.value.patch)
	const del = computed(() => http.value.delete)

	const init = function(apiUrl: string, token: string) {
		http.value = initAxios(apiUrl, token)
	}

	return { http, init, get, post, put, patch, del }
})
